
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {UpdateType} from '../core/UpdateType';
import {FieldModelUpdateType} from '../core/UpdateType';
import {Account} from '../core/Account';
import {FieldModelAccount} from '../core/Account';

/**
 * ArchivedAccountUpdateNotify struct
 */
class ArchivedAccountUpdateNotify {
  /**
   * Initialize struct
   * @param {!UpdateType=} updateType
   * @param {Account=} previous
   * @param {Account=} current
   * @param {!boolean=} isRestoration
   * @constructor
   */
  constructor (updateType = new UpdateType(), previous = null, current = null, isRestoration = false) {
    this.UpdateType = updateType
    this.Previous = previous
    this.Current = current
    this.IsRestoration = isRestoration
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ArchivedAccountUpdateNotify}
   * @param {!ArchivedAccountUpdateNotify} other Other struct
   * @returns {!ArchivedAccountUpdateNotify} This struct
   */
  copy (other) {
    if (other.UpdateType != null) {
      this.UpdateType = UpdateType.fromObject(other.UpdateType)
    } else {
      this.UpdateType = null
    }
    if (other.Previous != null) {
      this.Previous = Account.fromObject(other.Previous)
    } else {
      this.Previous = null
    }
    if (other.Current != null) {
      this.Current = Account.fromObject(other.Current)
    } else {
      this.Current = null
    }
    if (other.IsRestoration != null) {
      this.IsRestoration = other.IsRestoration
    } else {
      this.IsRestoration = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ArchivedAccountUpdateNotify}
   * @returns {!ArchivedAccountUpdateNotify} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ArchivedAccountUpdateNotifyModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ArchivedAccountUpdateNotifyModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ArchivedAccountUpdateNotify}
   * @param {!ArchivedAccountUpdateNotify} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ArchivedAccountUpdateNotify)) {
      throw new TypeError('Instance of ArchivedAccountUpdateNotify is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ArchivedAccountUpdateNotify}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      UpdateType: ((this.UpdateType != null) ? this.UpdateType : null),
      Previous: ((this.Previous != null) ? this.Previous : null),
      Current: ((this.Current != null) ? this.Current : null),
      IsRestoration: ((this.IsRestoration != null) ? this.IsRestoration : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ArchivedAccountUpdateNotify.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ArchivedAccountUpdateNotify} other Object value
   * @returns {!ArchivedAccountUpdateNotify} Created struct
   */
  static fromObject (other) {
    return new ArchivedAccountUpdateNotify().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ArchivedAccountUpdateNotify}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ArchivedAccountUpdateNotify.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ArchivedAccountUpdateNotify}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6649
  }
}

export { ArchivedAccountUpdateNotify };

/**
 * Fast Binary Encoding ArchivedAccountUpdateNotify field model
 */
class FieldModelArchivedAccountUpdateNotify extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.UpdateType = new FieldModelUpdateType(buffer, 4 + 4)
    this.Previous = new fbe.FieldModelOptional(new FieldModelAccount(buffer, this.UpdateType.fbeOffset + this.UpdateType.fbeSize), buffer, this.UpdateType.fbeOffset + this.UpdateType.fbeSize)
    this.Current = new fbe.FieldModelOptional(new FieldModelAccount(buffer, this.Previous.fbeOffset + this.Previous.fbeSize), buffer, this.Previous.fbeOffset + this.Previous.fbeSize)
    this.IsRestoration = new fbe.FieldModelBool(buffer, this.Current.fbeOffset + this.Current.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelArchivedAccountUpdateNotify}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelArchivedAccountUpdateNotify}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.UpdateType.fbeSize + this.Previous.fbeSize + this.Current.fbeSize + this.IsRestoration.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelArchivedAccountUpdateNotify}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.UpdateType.fbeExtra + this.Previous.fbeExtra + this.Current.fbeExtra + this.IsRestoration.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelArchivedAccountUpdateNotify}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelArchivedAccountUpdateNotify.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelArchivedAccountUpdateNotify}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6649
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelArchivedAccountUpdateNotify}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelArchivedAccountUpdateNotify}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.UpdateType.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.UpdateType.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UpdateType.fbeSize

    if ((fbeCurrentSize + this.Previous.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Previous.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Previous.fbeSize

    if ((fbeCurrentSize + this.Current.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Current.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Current.fbeSize

    if ((fbeCurrentSize + this.IsRestoration.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.IsRestoration.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IsRestoration.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelArchivedAccountUpdateNotify}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelArchivedAccountUpdateNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelArchivedAccountUpdateNotify}
   * @param {!ArchivedAccountUpdateNotify} fbeValue Default value, defaults is new ArchivedAccountUpdateNotify()
   * @returns {!ArchivedAccountUpdateNotify} ArchivedAccountUpdateNotify value
   */
  get (fbeValue = new ArchivedAccountUpdateNotify()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelArchivedAccountUpdateNotify}
   * @param {!ArchivedAccountUpdateNotify} fbeValue ArchivedAccountUpdateNotify value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.UpdateType.fbeSize) <= fbeStructSize) {
      fbeValue.UpdateType = this.UpdateType.get()
    } else {
      fbeValue.UpdateType = new UpdateType()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UpdateType.fbeSize

    if ((fbeCurrentSize + this.Previous.fbeSize) <= fbeStructSize) {
      fbeValue.Previous = this.Previous.get()
    } else {
      fbeValue.Previous = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Previous.fbeSize

    if ((fbeCurrentSize + this.Current.fbeSize) <= fbeStructSize) {
      fbeValue.Current = this.Current.get()
    } else {
      fbeValue.Current = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Current.fbeSize

    if ((fbeCurrentSize + this.IsRestoration.fbeSize) <= fbeStructSize) {
      fbeValue.IsRestoration = this.IsRestoration.get(false)
    } else {
      fbeValue.IsRestoration = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IsRestoration.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelArchivedAccountUpdateNotify}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelArchivedAccountUpdateNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelArchivedAccountUpdateNotify}
   * @param {!ArchivedAccountUpdateNotify} fbeValue ArchivedAccountUpdateNotify value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelArchivedAccountUpdateNotify}
   * @param {ArchivedAccountUpdateNotify} fbeValue ArchivedAccountUpdateNotify value
   */
  setFields (fbeValue) {
    this.UpdateType.set(fbeValue.UpdateType)
    this.Previous.set(fbeValue.Previous)
    this.Current.set(fbeValue.Current)
    this.IsRestoration.set(fbeValue.IsRestoration)
  }
}

export { FieldModelArchivedAccountUpdateNotify };

/**
 * Fast Binary Encoding ArchivedAccountUpdateNotify model
 */
class ArchivedAccountUpdateNotifyModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelArchivedAccountUpdateNotify(this.buffer, 4)
  }

  /**
   * Get the ArchivedAccountUpdateNotify model
   * @this {!ArchivedAccountUpdateNotifyModel}
   * @returns {!FieldModelArchivedAccountUpdateNotify} model ArchivedAccountUpdateNotify model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ArchivedAccountUpdateNotifyModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ArchivedAccountUpdateNotifyModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ArchivedAccountUpdateNotifyModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ArchivedAccountUpdateNotifyModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelArchivedAccountUpdateNotify.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ArchivedAccountUpdateNotifyModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ArchivedAccountUpdateNotifyModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ArchivedAccountUpdateNotifyModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ArchivedAccountUpdateNotifyModel}
   * @param {!ArchivedAccountUpdateNotify} value ArchivedAccountUpdateNotify value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ArchivedAccountUpdateNotifyModel}
   * @param {!ArchivedAccountUpdateNotify} value ArchivedAccountUpdateNotify value, defaults is new ArchivedAccountUpdateNotify()
   * @return {!object} Deserialized ArchivedAccountUpdateNotify value and its size
   */
  deserialize (value = new ArchivedAccountUpdateNotify()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ArchivedAccountUpdateNotify(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ArchivedAccountUpdateNotify(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ArchivedAccountUpdateNotifyModel}
   * @param {!number} prev Previous ArchivedAccountUpdateNotify model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ArchivedAccountUpdateNotifyModel };
